import type { DossierTypeRecord, ReasonRecord } from '@clim/domain';
import { useState } from 'react';
import { ErrorBox, Field, Input } from '../../components/ui';
import { useReady } from '../../lib/session';
import { useApi, useMutation } from './common';

const CATEGORY_LABEL: Record<string, string> = {
  case_return: 'Motifs de renvoi « À corriger »',
  case_cancel: 'Motifs d’annulation de dossier',
  wall_type: 'Types de mur / support',
  intervention_refusal: 'Motifs de refus d’intervention',
  intervention_reassign: 'Motifs de réattribution',
  intervention_impossible: 'Motifs « intervention impossible »',
  intervention_cancel: 'Motifs d’annulation d’intervention',
  sav_reason: 'Motifs de SAV',
  sav_cause: 'Causes de clôture SAV',
  reserve: 'Réserves / décharges (à valider juridiquement)',
  payment_cancel: 'Motifs d’annulation de paiement',
  photo_category: 'Catégories de photos',
  resource_category: 'Catégories de ressources (flyers, documents)',
  document_type: 'Types de documents',
  partner: 'Partenaires (entreprises pour qui nous posons en sous-traitance)',
  supplier: 'Fournisseurs (où les techniciens récupèrent le matériel : bons d’enlèvement)',
};

export function ReferentialsAdmin() {
  const types = useApi<DossierTypeRecord[]>('/v1/dossier-types');
  const reasons = useApi<ReasonRecord[]>('/v1/reasons');
  const m = useMutation();
  // les listes servent aussi hors ligne (fiches, dossiers) : on synchronise tout de suite après un changement
  const { engine } = useReady();
  const [t, setT] = useState({ code: '', label: '', activity: 'pose', requiresInstallConditions: false });
  const [r, setR] = useState({ category: 'case_cancel', code: '', label: '', requiresComment: false });
  const photoCats = (reasons.data ?? []).filter((x) => x.category === 'photo_category' && x.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const categories = [...new Set([...Object.keys(CATEGORY_LABEL), ...(reasons.data?.map((x) => x.category) ?? [])])];

  return (
    <div>
      <ErrorBox message={types.error ?? reasons.error ?? m.error} />
      <h2>Types de dossiers</h2>
      <ul className="list">
        {types.data?.map((x) => (
          <li key={x.id} className="row">
            <div className="row-main">
              <strong>{x.label}</strong>
              <span className="muted">{x.code}</span>
            </div>
            <label className="check">
              <input type="checkbox" checked={x.isActive} onChange={() => void m.run('PATCH', `/v1/admin/dossier-types/${x.id}`, { isActive: !x.isActive }).then(types.reload)} />
              actif
            </label>
            <div className="type-rules">
              <label className="check">
                <input
                  type="checkbox"
                  checked={!!x.settings.requiresInstallConditions}
                  onChange={(e) => void m.run('PATCH', `/v1/admin/dossier-types/${x.id}`, { settings: { ...x.settings, requiresInstallConditions: e.target.checked } }).then(types.reload)}
                />
                Conditions d’installation obligatoires
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={!!x.settings.requiresSignature}
                  onChange={(e) => void m.run('PATCH', `/v1/admin/dossier-types/${x.id}`, { settings: { ...x.settings, requiresSignature: e.target.checked } }).then(types.reload)}
                />
                Signature du client obligatoire
              </label>
              <span className="muted">Photos obligatoires avant soumission :</span>
              {photoCats.map((c) => {
                const required = Array.isArray(x.settings.requiredPhotoCategories) ? (x.settings.requiredPhotoCategories as string[]) : [];
                const on = required.includes(c.code);
                return (
                  <label key={c.id} className="check">
                    <input
                      type="checkbox"
                      aria-label={`${x.label} : photo ${c.label} obligatoire`}
                      checked={on}
                      onChange={() =>
                        void m
                          .run('PATCH', `/v1/admin/dossier-types/${x.id}`, {
                            settings: { ...x.settings, requiredPhotoCategories: on ? required.filter((r) => r !== c.code) : [...required, c.code] },
                          })
                          .then(types.reload)
                      }
                    />
                    {c.label}
                  </label>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      <div className="card form inline">
        <Field label="Libellé">
          <Input value={t.label} onChange={(e) => setT({ ...t, label: e.target.value, code: e.target.value.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') })} />
        </Field>
        <Field label="Activité (signature visuelle)">
          <select className="input" value={t.activity} onChange={(e) => setT({ ...t, activity: e.target.value })}>
            <option value="vente">Vente</option>
            <option value="pose">Pose</option>
            <option value="entretien">Entretien</option>
            <option value="sav">SAV</option>
          </select>
        </Field>
        <label className="check">
          <input type="checkbox" checked={t.requiresInstallConditions} onChange={(e) => setT({ ...t, requiresInstallConditions: e.target.checked })} />
          Conditions d’installation obligatoires
        </label>
        <button
          className="btn primary"
          disabled={!t.label}
          onClick={() =>
            void m
              .run('POST', '/v1/admin/dossier-types', {
                code: t.code,
                label: t.label,
                settings: { activity: t.activity, requiresInstallConditions: t.requiresInstallConditions },
              })
              .then(() => {
                setT({ code: '', label: '', activity: 'pose', requiresInstallConditions: false });
                types.reload();
              }, () => undefined)
          }
        >
          Ajouter
        </button>
      </div>

      <h2>Motifs et listes de choix</h2>
      {categories.map((cat) => (
        <div key={cat} className="card">
          <h3>{CATEGORY_LABEL[cat] ?? cat}</h3>
          <ul className="plain">
            {reasons.data?.filter((x) => x.category === cat).map((x) => (
              <li key={x.id} className="row">
                <span className="row-main">
                  {x.label} {x.requiresComment && <span className="muted">(commentaire obligatoire)</span>}
                </span>
                <label className="check">
                  <input type="checkbox" checked={x.isActive} onChange={() => void m.run('PATCH', `/v1/admin/reasons/${x.id}`, { isActive: !x.isActive }).then(() => { reasons.reload(); void engine.sync(); })} />
                  actif
                </label>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="card form">
        <div className="grid2">
          <Field label="Catégorie">
            <select className="input" value={r.category} onChange={(e) => setR({ ...r, category: e.target.value })}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c] ?? c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Libellé">
            <Input value={r.label} onChange={(e) => setR({ ...r, label: e.target.value, code: e.target.value.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') })} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={r.requiresComment} onChange={(e) => setR({ ...r, requiresComment: e.target.checked })} />
          Commentaire obligatoire
        </label>
        <button className="btn primary" disabled={!r.label} onClick={() => void m.run('POST', '/v1/admin/reasons', r).then(() => { setR({ ...r, code: '', label: '' }); reasons.reload(); void engine.sync(); }, () => undefined)}>
          Ajouter le motif
        </button>
      </div>
    </div>
  );
}
